import { Menu, PanelLeftClose, PanelLeft } from 'lucide-react';
import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../lib/cn';
import { Button } from './button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from './sheet';
import { ThemeToggle } from './theme-toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

export interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Matches only the exact path. Set on index routes. */
  end?: boolean;
}

export interface AppShellProps {
  /** The wordmark. */
  brand: ReactNode;
  /** Sits beside the wordmark — this is where admin declares itself. */
  brandBadge?: ReactNode;
  nav: NavItem[];
  /** Identity block and sign-out, pinned to the bottom of the sidebar. */
  sidebarFooter?: ReactNode;
  /** Rendered in the top bar, left of the theme toggle. */
  topBar?: ReactNode;
  /**
   * A persistent band across the top. Admin uses it to be unmistakable: the
   * old code made the same point with a single badge, and the point is worth
   * more than a badge — knowing which app you are in before you click
   * "suspend" is the whole reason the two are separate bundles.
   */
  banner?: ReactNode;
  children: ReactNode;
}

const COLLAPSE_KEY = 'xinfer-sidebar-collapsed';

export function AppShell({
  brand,
  brandBadge,
  nav,
  sidebarFooter,
  topBar,
  banner,
  children,
}: AppShellProps) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      // A denied store costs the preference on reload, nothing else.
    }
  }, [collapsed]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {banner}

      <div className="flex flex-1">
        {/* Desktop sidebar. Hidden below `md`, where it becomes a Sheet. */}
        <aside
          className={cn(
            'sticky top-0 hidden h-screen shrink-0 flex-col border-r border-border bg-card md:flex',
            collapsed ? 'w-16' : 'w-56',
          )}
        >
          <Brand brand={brand} badge={brandBadge} collapsed={collapsed} />
          <Nav items={nav} collapsed={collapsed} />
          <div className={cn('mt-auto border-t border-border p-3', collapsed && 'px-2')}>
            {!collapsed && sidebarFooter}
            <Button
              variant="ghost"
              size={collapsed ? 'icon' : 'sm'}
              onClick={() => setCollapsed((value) => !value)}
              className={cn('mt-2', !collapsed && 'w-full justify-start')}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <PanelLeft /> : <PanelLeftClose />}
              {!collapsed && <span>Collapse</span>}
            </Button>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background/85 px-4 backdrop-blur">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation">
                  <Menu />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <Brand brand={brand} badge={brandBadge} collapsed={false} />
                {/* Closing on navigate is the difference between a drawer and
                    a drawer that covers the page you just asked for. */}
                <div onClick={() => setMobileOpen(false)}>
                  <Nav items={nav} collapsed={false} />
                </div>
                <div className="mt-auto border-t border-border p-3">{sidebarFooter}</div>
              </SheetContent>
            </Sheet>

            <div className="flex min-w-0 flex-1 items-center gap-3">{topBar}</div>
            <ThemeToggle />
          </header>

          <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto w-full max-w-6xl">{children}</div>
          </main>
        </div>
      </div>
    </div>
  );
}

function Brand({
  brand,
  badge,
  collapsed,
}: {
  brand: ReactNode;
  badge?: ReactNode;
  collapsed: boolean;
}) {
  return (
    <div
      className={cn(
        'flex h-14 shrink-0 items-center gap-2 border-b border-border px-4',
        collapsed && 'justify-center px-0',
      )}
    >
      <span className="truncate text-sm font-semibold tracking-tight">{brand}</span>
      {!collapsed && badge}
    </div>
  );
}

function Nav({ items, collapsed }: { items: NavItem[]; collapsed: boolean }) {
  return (
    <nav className={cn('flex flex-col gap-0.5 p-2', collapsed && 'items-center')}>
      {items.map((item) => {
        const link = (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                collapsed && 'justify-center px-0 size-9',
                // `aria-current="page"` comes from NavLink itself; this is the
                // visual half of the same statement.
                isActive && 'bg-accent text-accent-foreground',
              )
            }
          >
            <item.icon className="size-4 shrink-0" />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </NavLink>
        );

        // Collapsed to icons, the label has to live somewhere or the sidebar
        // becomes a memory test.
        return collapsed ? (
          <Tooltip key={item.to}>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right">{item.label}</TooltipContent>
          </Tooltip>
        ) : (
          link
        );
      })}
    </nav>
  );
}
